/**
 * SEE-1350: does the account Microsoft authenticated match what the user
 * typed? Pure helpers for the post-login verification gate.
 *
 * The typed value is compared against THREE identifiers from the Cognito ID
 * token, all lowercased:
 *   1. the `email` claim,
 *   2. the `preferred_username` claim,
 *   3. the SAML NameID embedded in `cognito:username`
 *      (format `{providerName}_{NameID}` — strip through the FIRST underscore;
 *      NameIDs themselves may contain underscores).
 * The third is load-bearing: Whitaker's IdP maps preferred_username to the
 * Entra object-id GUID, and their NameIDs are the employee-number UPNs
 * (w…@whitcon.com) — so users typing either the UPN or the real email match,
 * and the mismatch screen only fires for a genuinely different account.
 */

export interface SsoIdTokenClaims {
  email?: string;
  preferred_username?: string;
  'cognito:username'?: string;
  identities?: unknown;
}

export interface SsoIdentifiers {
  email?: string;
  preferredUsername?: string;
  nameId?: string;
}

const normalize = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : undefined;
};

export function extractSsoIdentifiers(claims: SsoIdTokenClaims | null | undefined): SsoIdentifiers {
  if (!claims) return {};
  const username = normalize(claims['cognito:username']);
  let nameId: string | undefined;
  if (username) {
    const separator = username.indexOf('_');
    if (separator > 0 && separator < username.length - 1) {
      nameId = username.slice(separator + 1);
    }
  }
  return {
    email: normalize(claims.email),
    preferredUsername: normalize(claims.preferred_username),
    nameId,
  };
}

/** True when the typed value matches ANY of the three identifiers. */
export function matchesTypedIdentifier(
  typed: string | null | undefined,
  identifiers: SsoIdentifiers
): boolean {
  const normalizedTyped = normalize(typed);
  if (!normalizedTyped) return false;
  return [identifiers.email, identifiers.preferredUsername, identifiers.nameId].some(
    (identifier) => identifier !== undefined && identifier === normalizedTyped
  );
}

/**
 * Human-readable name for the authenticated account, for the mismatch screen.
 * Prefers the email claim; falls back to any @-shaped identifier. Returns
 * undefined when nothing displayable exists — callers must then FAIL OPEN
 * (no interstitial): showing "signed in as <GUID>" would be worse than none.
 */
export function authenticatedDisplayIdentity(identifiers: SsoIdentifiers): string | undefined {
  if (identifiers.email) return identifiers.email;
  if (identifiers.preferredUsername?.includes('@')) return identifiers.preferredUsername;
  if (identifiers.nameId?.includes('@')) return identifiers.nameId;
  return undefined;
}
